import React, { useState, useEffect } from 'react';
import { Typography, Button, message } from 'antd';
import { Play, Copy, Check, X } from 'lucide-react';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  dadanExportResultAtom,
  resetDadanExportStateAtom,
} from '../../atoms/exportAtoms';
import { onboardingService } from '../../services/onboardingService';
import './export.scss';

const { Text, Title } = Typography;

interface ExportSuccessProps {
  onClose: () => void;
}

const ExportSuccess: React.FC<ExportSuccessProps> = ({ onClose }) => {
  const result = useAtomValue(dadanExportResultAtom);
  const resetExportState = useSetAtom(resetDadanExportStateAtom);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (result?.success) {
      onboardingService.markExportShare().catch(() => {});
    }
  }, [result?.success]);

  const handleWatchOnDadan = () => {
    if (result?.watchUrl) {
      window.open(result.watchUrl, '_blank');
    }
  };

  const handleCopyShareLink = async () => {
    if (result?.shareUrl) {
      try {
        await navigator.clipboard.writeText(result.shareUrl);
        setCopied(true);
        message.success('Link copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        message.error('Failed to copy link');
      }
    }
  };

  const handleClose = () => {
    resetExportState();
    onClose();
  };

  if (!result?.success) {
    return null;
  }

  return (
    <div className="export-success">
      <button 
        className="export-success__close"
        onClick={handleClose}
        aria-label="Close"
      >
        <X size={20} />
      </button>
      
      <div className="export-success__header">
        <div className="export-success__icon-wrapper">
          <Check size={28} strokeWidth={3} />
        </div>
        <Title level={4} className="export-success__title">
          Export Complete
        </Title>
        <Text type="secondary" className="export-success__subtitle">
          Your video has been uploaded to your Personal Library
        </Text>
      </div>

      <div className="export-success__actions">
        <Button
          type="primary"
          size="large"
          icon={<Play size={16} fill="currentColor" />}
          onClick={handleWatchOnDadan}
          className="export-success__primary-btn"
          block
        >
          Watch Video
        </Button>

        <Button
          size="large"
          icon={copied ? <Check size={16} /> : <Copy size={16} />}
          onClick={handleCopyShareLink}
          className="export-success__secondary-btn"
          block
        >
          {copied ? 'Copied!' : 'Copy Share Link'}
        </Button>
      </div>
    </div>
  );
};

export default ExportSuccess;
